import { useEditorFeature, useSandboxEditor } from "features/editor/Feature";
import type {
  Visibility,
  VisibilityNumber,
} from "features/editor/sandboxUtils";
import {
  numberToVisibility,
  visibilityToNumber,
} from "features/editor/sandboxUtils";
import { Box, Icon, Text, Stack, Select } from "prism-react";
import { useChangeSandboxInfo } from "queries/sandbox/api";
import type { FC } from "react";

import { useActiveWorkspace } from "hooks/useActiveWorkspace";
import { useUserWorkspaces } from "hooks/useUserWorkspaces";

const captions: Record<Visibility, string> = {
  public: "Everyone has access",
  private: "Only workspace members have access",
  unlisted: "Everyone with the link can access",
};

const VisibilityItem: FC<{
  value: Visibility;
  label: string;
  disabledReason?: string;
  selected: boolean;
}> = ({ value, label, disabledReason, selected }) => {
  return (
    <Select.Item
      disabled={!!disabledReason}
      itemIndicatorPosition="none"
      value={value}
    >
      <Stack horizontal>
        {selected ? (
          <Icon size={3} type="check" />
        ) : (
          <Box css={{ width: "$3" }} />
        )}

        <Stack horizontal>
          <Text
            css={{ color: disabledReason ? "$neutral-fg-base" : undefined }}
          >
            {label}
          </Text>
          <Text
            as="span"
            css={{
              color: disabledReason
                ? "$informative-warning-base"
                : "$neutral-fg-base",
            }}
          >
            {disabledReason ? disabledReason : captions[value]}
          </Text>
        </Stack>
      </Stack>
    </Select.Item>
  );
};

export const SandboxVisibilityUI: FC<{
  sandboxVisibility: Visibility;
  onVisibilityChange: (visibility: Visibility) => void;
  disabled?: boolean;
  error?: string;
  variant?: "solid";
  workspaceId?: string;
}> = ({
  sandboxVisibility,
  onVisibilityChange,
  disabled,
  error,
  variant = "solid",
  workspaceId,
}) => {
  const { workspaces } = useUserWorkspaces();
  const activeWorkspace = useActiveWorkspace();
  const workspace =
    workspaces.find((w) => w.id === workspaceId) ?? activeWorkspace;

  if (!workspace) {
    return;
  }

  return (
    <Stack gap={1}>
      <Select
        defaultValue={sandboxVisibility}
        disabled={disabled}
        onValueChange={(value) => {
          onVisibilityChange(value as Visibility);
        }}
        variant={variant}
      >
        <VisibilityItem
          label={getLabelByVisibilityValue("public")}
          selected={sandboxVisibility === "public"}
          value="public"
        />
        <VisibilityItem
          label={getLabelByVisibilityValue("unlisted")}
          selected={sandboxVisibility === "unlisted"}
          value="unlisted"
        />
        <VisibilityItem
          label={getLabelByVisibilityValue("private")}
          selected={sandboxVisibility === "private"}
          value="private"
        />
      </Select>
      {error && <Text color="informative-error-base">{error}</Text>}
    </Stack>
  );
};

export const SandboxVisbility = ({
  hasPermission,
}: {
  hasPermission: boolean;
}) => {
  const project = useSandboxEditor();
  const [, projectDispatcher] = useEditorFeature();
  const updateInfo = useChangeSandboxInfo(project.id);
  const { refetch } = useUserWorkspaces();

  // 0 is public, 1 is unlisted, 2 is private
  const privacy = project.privacy as VisibilityNumber;

  return (
    <SandboxVisibilityUI
      disabled={!hasPermission}
      onVisibilityChange={async (visibility) => {
        const privacyNumber = visibilityToNumber(visibility);

        const oldPrivacy = project.privacy;

        projectDispatcher({
          type: "UPDATE_SANDBOX",
          project: {
            privacy: privacyNumber,
          },
        });
        try {
          await updateInfo({ privacy: privacyNumber });
        } catch {
          projectDispatcher({
            type: "UPDATE_SANDBOX",
            project: {
              privacy: oldPrivacy,
            },
          });
        }

        refetch();
      }}
      sandboxVisibility={numberToVisibility(privacy)}
    />
  );
};

const getLabelByVisibilityValue = (visibility: Visibility) => {
  switch (visibility) {
    case "public":
      return "Public";
    case "private":
      return "Private";
    case "unlisted":
      return "Unlisted";
  }
};
